/**
 * Пример плагина для логирования операций CSElement
 */

import { CSElementPlugin, MiddlewareContext } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';

export interface LoggingOptions {
  /** Включить логирование создания элементов */
  logCreate?: boolean;
  
  /** Включить логирование операций с данными */
  logData?: boolean;
  
  /** Включить логирование операций с элементами */
  logElements?: boolean;
  
  /** Включить логирование уничтожения */
  logDestroy?: boolean;
  
  /** Включить логирование производительности */
  logPerformance?: boolean;
  
  /** Префикс для логов */
  prefix?: string;
  
  /** Уровень детализации */
  level?: 'basic' | 'detailed' | 'verbose';
}

export class LoggingPlugin implements CSElementPlugin {
  readonly name = 'logging';
  readonly version = '1.0.0';
  readonly description = 'Плагин для логирования операций CSElement';

  private options: Required<LoggingOptions>;

  constructor(options: LoggingOptions = {}) {
    this.options = {
      logCreate: true,
      logData: true,
      logElements: true,
      logDestroy: true,
      logPerformance: true,
      prefix: '[CSElement]',
      level: 'basic',
      ...options
    };
  }

  install(CSElementClass: any, options?: any): void {
    const mergedOptions = { ...this.options, ...options };
    this.options = mergedOptions;

    // Получаем менеджер плагинов
    const pluginManager = CSElementClass.plugins;

    // Регистрируем хуки жизненного цикла
    if (this.options.logCreate) {
      pluginManager.addHook('afterCreate', this.logElementCreated.bind(this));
    }

    if (this.options.logData) {
      pluginManager.addHook('beforeSetData', this.logBeforeSetData.bind(this));
      pluginManager.addHook('afterSetData', this.logAfterSetData.bind(this));
      pluginManager.addHook('beforeDeleteData', this.logBeforeDeleteData.bind(this));
      pluginManager.addHook('afterDeleteData', this.logAfterDeleteData.bind(this));
    }

    if (this.options.logElements) {
      pluginManager.addHook('beforeAddElement', this.logBeforeAddElement.bind(this));
      pluginManager.addHook('afterAddElement', this.logAfterAddElement.bind(this));
      pluginManager.addHook('beforeRemoveElement', this.logBeforeRemoveElement.bind(this));
      pluginManager.addHook('afterRemoveElement', this.logAfterRemoveElement.bind(this));
    }

    if (this.options.logDestroy) {
      pluginManager.addHook('beforeDestroy', this.logBeforeDestroy.bind(this));
      pluginManager.addHook('afterDestroy', this.logAfterDestroy.bind(this));
    }

    // Регистрируем middleware для измерения производительности
    if (this.options.logPerformance) {
      const performanceMiddlewareConfig = {
        name: 'loggingPerformance',
        middleware: this.performanceMiddleware.bind(this)
      };
      pluginManager.addAdvancedMiddleware('setData', performanceMiddlewareConfig);
      pluginManager.addAdvancedMiddleware('deleteData', performanceMiddlewareConfig);
      pluginManager.addAdvancedMiddleware('addElement', performanceMiddlewareConfig);
      pluginManager.addAdvancedMiddleware('removeElement', performanceMiddlewareConfig);
    }

    this.log('Плагин логирования установлен', 'info');
  }

  uninstall(): void {
    this.log('Плагин логирования удален', 'info');
  }

  // Хуки жизненного цикла
  private logElementCreated(element: ICSElement): void {
    this.log(`Создан элемент: ${element.name} (ID: ${element.id})`, 'create');
  }

  private logBeforeSetData(element: ICSElement, key: string, value: any): void {
    if (this.options.level === 'verbose') {
      this.log(`[${element.name}] Установка данных: ${key} = ${JSON.stringify(value)}`, 'data');
    }
  }

  private logAfterSetData(element: ICSElement, key: string, _value: any): void {
    if (this.options.level !== 'basic') {
      this.log(`[${element.name}] Данные установлены: ${key}`, 'data');
    }
  }

  private logBeforeDeleteData(element: ICSElement, key: string): void {
    if (this.options.level === 'verbose') {
      this.log(`[${element.name}] Удаление данных: ${key}`, 'data');
    }
  }

  private logAfterDeleteData(element: ICSElement, key: string): void {
    if (this.options.level !== 'basic') {
      this.log(`[${element.name}] Данные удалены: ${key}`, 'data');
    }
  }

  private logBeforeAddElement(parent: ICSElement, child: ICSElement): void {
    this.log(`[${parent.name}] Добавление дочернего элемента: ${child.name}`, 'element');
  }

  private logAfterAddElement(parent: ICSElement, child: ICSElement): void {
    if (this.options.level !== 'basic') {
      this.log(`[${parent.name}] Дочерний элемент добавлен: ${child.name}`, 'element');
    }
  }

  private logBeforeRemoveElement(parent: ICSElement, child: ICSElement): void {
    this.log(`[${parent.name}] Удаление дочернего элемента: ${child.name}`, 'element');
  }

  private logAfterRemoveElement(parent: ICSElement, child: ICSElement): void {
    if (this.options.level !== 'basic') {
      this.log(`[${parent.name}] Дочерний элемент удален: ${child.name}`, 'element');
    }
  }

  private logBeforeDestroy(element: ICSElement): void {
    this.log(`Уничтожение элемента: ${element.name} (ID: ${element.id})`, 'destroy');
  }

  private logAfterDestroy(element: ICSElement): void {
    if (this.options.level === 'verbose') {
      this.log(`Элемент уничтожен: ${element.name}`, 'destroy');
    }
  }

  // Middleware для производительности
  private async performanceMiddleware(
    context: MiddlewareContext,
    next: () => Promise<any>
  ): Promise<any> {
    const startTime = Date.now();
    
    try {
      const result = await next();
      const duration = Date.now() - startTime;
      
      if (this.options.level === 'verbose' || duration > 100) {
        this.log(
          `[${context.element.name}] ${context.operation} выполнен за ${duration}мс`,
          'performance'
        );
      }
      
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.log(
        `[${context.element.name}] ${context.operation} завершился с ошибкой за ${duration}мс: ${error}`,
        'error'
      );
      throw error;
    }
  }

  // Утилиты логирования
  private log(message: string, type: string): void {
    const timestamp = new Date().toISOString();
    const formattedMessage = `${this.options.prefix} [${type.toUpperCase()}] ${timestamp} - ${message}`;
    
    switch (type) {
      case 'error':
        console.error(formattedMessage);
        break;
      case 'performance':
        console.log(formattedMessage);
        break;
      default:
        console.log(formattedMessage);
    }
  }

  // Публичные методы для управления логированием
  setLevel(level: 'basic' | 'detailed' | 'verbose'): void {
    this.options.level = level;
    this.log(`Уровень логирования изменен на: ${level}`, 'info');
  }

  setPrefix(prefix: string): void {
    this.options.prefix = prefix;
    this.log(`Префикс логирования изменен на: ${prefix}`, 'info');
  }

  getStats(): {
    level: string;
    prefix: string;
    enabledFeatures: string[];
  } {
    const enabledFeatures: string[] = [];
    
    if (this.options.logCreate) enabledFeatures.push('create');
    if (this.options.logData) enabledFeatures.push('data');
    if (this.options.logElements) enabledFeatures.push('elements');
    if (this.options.logDestroy) enabledFeatures.push('destroy');
    if (this.options.logPerformance) enabledFeatures.push('performance');

    return {
      level: this.options.level,
      prefix: this.options.prefix,
      enabledFeatures
    };
  }
} 